"use client";

import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckCircle2,
  ChevronRight,
  Code,
  Loader2,
  Table,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import * as React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/atoms/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/atoms/collapsible";
import { CopyButton } from "@/components/ui/atoms/copy-button";
import { JsonHighlight } from "@/components/ui/atoms/json-highlight";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToolCallView = "table" | "code";

export type ToolCallState = "calling" | "complete" | "error";

export type ToolCallAccordionProps = React.ComponentProps<typeof Accordion>;

export type ToolCallAccordionItemProps = React.ComponentProps<
  typeof AccordionItem
>;

export type ToolCallAccordionTriggerProps = React.ComponentProps<
  typeof AccordionTrigger
> &
  VariantProps<typeof toolCallAccordionTriggerVariants> & {
    toolName: string;
    icon?: React.ReactNode;
    showStateIndicator?: boolean;
  };

export type ToolCallAccordionContentProps = React.ComponentProps<
  typeof AccordionContent
> & {
  view?: ToolCallView;
  onViewChange?: (view: ToolCallView) => void;
  showViewToggle?: boolean;
  args?: Record<string, unknown>;
  result?: unknown;
  errorNotice?: {
    title: string;
    description: string;
    details?: string;
  };
};

// ---------------------------------------------------------------------------
// CVA variants
// ---------------------------------------------------------------------------

const toolCallAccordionTriggerVariants = cva(
  "font-mono text-xs transition-colors",
  {
    variants: {
      state: {
        calling: "text-muted-foreground hover:text-foreground",
        complete: "text-muted-foreground hover:text-foreground",
        error: "text-destructive hover:text-destructive/80",
      },
    },
    defaultVariants: { state: "calling" },
  }
);

const toolCallAccordionStateIndicatorVariants = cva(
  "absolute -top-0.5 right-0 size-2 shrink-0 rounded-full ring-2 ring-background",
  {
    variants: {
      state: {
        calling: "animate-pulse bg-orange-400",
        complete: "bg-green-500",
        error: "bg-destructive",
      },
    },
    defaultVariants: { state: "calling" },
  }
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function truncateValue(value: string, maxLength = 140): string {
  return value.length > maxLength
    ? `${value.slice(0, maxLength).trimEnd()}...`
    : value;
}

function toCodeString(value: unknown): string {
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2) ?? "null";
}

function toTableData(value: unknown): Record<string, unknown> {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        return parsed as Record<string, unknown>;
      }
    } catch {
      return { value };
    }
    return { value };
  }
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    return value as Record<string, unknown>;
  }
  return { value };
}

function formatTableValue(value: unknown): string {
  if (typeof value === "string") {
    return truncateValue(value.replace(/\s+/g, " "));
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null ||
    value === undefined
  ) {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  }
  if (typeof value === "object") {
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const count = Object.keys(value as Record<string, unknown>).length;
    return `${count} field${count === 1 ? "" : "s"}`;
  }
  return String(value);
}

function isExpandableTableValue(value: unknown): boolean {
  return (
    (typeof value === "string" && value.length > 180) ||
    Array.isArray(value) ||
    (typeof value === "object" && value !== null)
  );
}

// ---------------------------------------------------------------------------
// Internal components
// ---------------------------------------------------------------------------

function ViewPanel({
  activeView,
  panelView,
  children,
  lazy = false,
}: {
  activeView: ToolCallView;
  panelView: ToolCallView;
  children: React.ReactNode;
  lazy?: boolean;
}) {
  const isActive = activeView === panelView;
  const [hasRendered, setHasRendered] = React.useState(isActive || !lazy);

  React.useEffect(() => {
    if (isActive) {
      setHasRendered(true);
    }
  }, [isActive]);

  if (!hasRendered) {
    return null;
  }
  return <div className={cn(!isActive && "hidden")}>{children}</div>;
}

function TableRow({ label, value }: { label: string; value: unknown }) {
  const [, startTransition] = React.useTransition();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isExpandable = isExpandableTableValue(value);

  return (
    <div className="grid gap-1 px-3 py-2.5 sm:grid-cols-[minmax(0,8rem)_minmax(0,1fr)] sm:gap-3">
      <dt className="font-medium text-[11px] text-muted-foreground uppercase tracking-[0.08em]">
        {label}
      </dt>
      <dd className="min-w-0 space-y-2 text-foreground/90 text-xs">
        <span className="wrap-break-word block whitespace-pre-wrap">
          {formatTableValue(value)}
        </span>
        {isExpandable && (
          <button
            className="inline-flex items-center rounded-md border border-border/60 px-2 py-1 font-medium text-[11px] text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground dark:border-white/10"
            onClick={() => startTransition(() => setIsExpanded((c) => !c))}
            type="button"
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        )}
        {isExpanded && (
          <JsonHighlight
            className="rounded-lg border border-border/60 bg-background/80 dark:border-white/10"
            code={toCodeString(value)}
          />
        )}
      </dd>
    </div>
  );
}

const DataTable = React.memo(function DataTable({ value }: { value: unknown }) {
  const entries = React.useMemo(
    () => Object.entries(toTableData(value)),
    [value]
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-background/80 dark:border-white/10">
      <dl className="divide-y divide-border/50 dark:divide-white/10">
        {entries.map(([key, entryValue]) => (
          <TableRow key={key} label={key} value={entryValue} />
        ))}
      </dl>
    </div>
  );
});

const Section = React.memo(function Section({
  label,
  value,
  view = "table",
  className,
  ...props
}: {
  label: string;
  value: unknown;
  view?: ToolCallView;
} & React.ComponentProps<"section">) {
  const copyContent = React.useMemo(() => toCodeString(value), [value]);

  return (
    <section className={cn("space-y-2", className)} {...props}>
      <div className="flex items-center justify-between gap-3">
        <h4 className="font-medium text-foreground text-xs">{label}</h4>
        <CopyButton
          className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
          content={copyContent}
          size="icon"
          variant="ghost"
        />
      </div>
      <ViewPanel activeView={view} panelView="table">
        <DataTable value={value} />
      </ViewPanel>
      <ViewPanel activeView={view} lazy panelView="code">
        <JsonHighlight
          className="rounded-lg border border-border/60 bg-background/80 dark:border-white/10"
          code={copyContent}
        />
      </ViewPanel>
    </section>
  );
});

function ToolCallErrorNotice({
  title,
  description,
  details,
}: {
  title: string;
  description: string;
  details?: string;
}) {
  return (
    <Collapsible
      className="rounded-lg border border-destructive/25 bg-destructive/[0.04] dark:border-destructive/20 dark:bg-destructive/[0.06]"
      data-slot="tool-call-error-notice"
      disabled={!details}
    >
      <CollapsibleTrigger
        className={cn(
          "group flex w-full items-start gap-2.5 px-3 py-2.5 text-left",
          details && "cursor-pointer"
        )}
      >
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <p className="font-medium text-foreground text-sm">{title}</p>
          <p className="text-muted-foreground text-xs">{description}</p>
        </div>
        {details && (
          <>
            <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground group-data-[state=open]:hidden">
              Details
              <ChevronRight className="size-3" />
            </span>
            <span className="mt-0.5 hidden shrink-0 items-center gap-1 text-[11px] text-muted-foreground group-data-[state=open]:inline-flex">
              Hide
              <ChevronRight className="size-3 rotate-90" />
            </span>
          </>
        )}
      </CollapsibleTrigger>
      {details && (
        <CollapsibleContent>
          <div className="relative rounded-b-lg border-border/50 border-t bg-card px-3 py-2.5 dark:border-white/10">
            <CopyButton
              className="absolute top-2 right-2 size-6 text-muted-foreground/60 hover:text-muted-foreground"
              content={details}
              size="icon"
              variant="ghost"
            />
            <pre className="wrap-break-word overflow-auto whitespace-pre-wrap pr-8 font-mono text-[11px] text-muted-foreground leading-relaxed">
              {details}
            </pre>
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// Public components
// ---------------------------------------------------------------------------

function ToolCallAccordion({ className, ...props }: ToolCallAccordionProps) {
  return (
    <Accordion
      className={cn("w-full", className)}
      data-slot="tool-call-accordion"
      {...props}
    />
  );
}

function ToolCallAccordionItem({
  className,
  ...props
}: ToolCallAccordionItemProps) {
  return (
    <AccordionItem
      className={cn(
        "w-full overflow-hidden rounded-xl border border-border/50 bg-card/75 shadow-sm last:border-b dark:border-white/10 dark:shadow-black/20",
        className
      )}
      data-slot="tool-call-accordion-item"
      {...props}
    />
  );
}

function ToolCallAccordionTrigger({
  className,
  state,
  toolName,
  icon,
  showStateIndicator = true,
  ...props
}: ToolCallAccordionTriggerProps) {
  const resolvedState = state ?? "calling";
  const triggerIcon = icon ?? (
    <Wrench className="size-3.5 text-muted-foreground" />
  );
  const statusIcon = {
    calling: (
      <Loader2 className="size-3 shrink-0 animate-spin text-orange-500" />
    ),
    complete: <CheckCircle2 className="size-3 shrink-0 text-green-500" />,
    error: <TriangleAlert className="size-3 shrink-0 text-destructive" />,
  } satisfies Record<ToolCallState, React.ReactNode>;
  const statusLabel = {
    calling: "Running",
    complete: "Completed",
    error: "Failed",
  } satisfies Record<ToolCallState, string>;

  return (
    <AccordionTrigger
      className={cn(
        "items-center gap-3 px-3 py-3 hover:bg-muted/15 hover:no-underline [&>svg]:translate-y-0 [&>svg]:self-center",
        toolCallAccordionTriggerVariants({ state: resolvedState }),
        className
      )}
      data-slot="tool-call-accordion-trigger"
      {...props}
    >
      <div className="relative shrink-0">
        <div className="flex size-8 items-center justify-center rounded-full bg-muted/45 ring-1 ring-border/50">
          {triggerIcon}
        </div>
        {showStateIndicator && (
          <span
            aria-hidden="true"
            className={toolCallAccordionStateIndicatorVariants({
              state: resolvedState,
            })}
          />
        )}
      </div>
      <div className="min-w-0 flex-1 text-left">
        <span className="block truncate text-foreground text-xs">
          {toolName}
        </span>
      </div>
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 self-center text-[11px] text-muted-foreground leading-none",
          resolvedState === "error" && "text-destructive"
        )}
      >
        {statusIcon[resolvedState]}
        {statusLabel[resolvedState]}
      </span>
    </AccordionTrigger>
  );
}

/**
 * Expandable content area with a built-in table/JSON view toggle.
 *
 * Pass `args` and/or `result` as props to render the standard "Parameters"
 * and "Response" sections. The active view is applied automatically.
 * Additional content can be passed as `children`.
 */
function ToolCallAccordionContent({
  className,
  children,
  view: viewProp,
  onViewChange,
  showViewToggle = true,
  args,
  result,
  errorNotice,
  ...props
}: ToolCallAccordionContentProps) {
  const [internalView, setInternalView] = React.useState<ToolCallView>("table");
  const [, startTransition] = React.useTransition();
  const view = viewProp ?? internalView;

  const handleViewChange = (nextView: ToolCallView) => {
    onViewChange?.(nextView);
    if (viewProp === undefined) {
      startTransition(() => setInternalView(nextView));
    }
  };

  return (
    <AccordionContent
      className="w-full border-border/50 border-t bg-muted/10 dark:border-white/10"
      data-slot="tool-call-accordion-content"
      {...props}
    >
      <div className={cn("space-y-3 overflow-auto px-3 py-3", className)}>
        {showViewToggle && (
          <div className="flex items-center justify-between gap-3">
            <p className="truncate text-[11px] text-muted-foreground">
              Inspect payload and response
            </p>
            <div className="inline-flex shrink-0 items-center gap-0.5 rounded-lg border border-border/60 bg-background/70 p-0.5 shadow-sm dark:border-white/10">
              <button
                className={cn(
                  "inline-flex h-6.5 items-center justify-center gap-1.5 rounded-md px-2.5 text-[11px] transition-colors",
                  view === "table"
                    ? "bg-background text-foreground shadow-sm ring-1 ring-border/60"
                    : "text-muted-foreground hover:bg-background/60 hover:text-foreground"
                )}
                onClick={() => handleViewChange("table")}
                type="button"
              >
                <Table className="size-3.5" />
                Table
              </button>
              <button
                className={cn(
                  "inline-flex h-6.5 items-center justify-center gap-1.5 rounded-md px-2.5 text-[11px] transition-colors",
                  view === "code"
                    ? "bg-background text-foreground shadow-sm ring-1 ring-border/60"
                    : "text-muted-foreground hover:bg-background/60 hover:text-foreground"
                )}
                onClick={() => handleViewChange("code")}
                type="button"
              >
                <Code className="size-3.5" />
                JSON
              </button>
            </div>
          </div>
        )}
        <div className="space-y-3">
          {children}
          {errorNotice && <ToolCallErrorNotice {...errorNotice} />}
          {args !== undefined && (
            <Section label="Parameters" value={args} view={view} />
          )}
          {result !== undefined && (
            <Section label="Response" value={result} view={view} />
          )}
        </div>
      </div>
    </AccordionContent>
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  ToolCallAccordion,
  ToolCallAccordionContent,
  ToolCallAccordionItem,
  ToolCallAccordionTrigger,
};
